import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getDefaultRoute } from "../../routes/defaultRoute";
import { getPanelUrl } from "../../config";

const isTokenError = error => {
	const errorMsg = error?.response?.data?.message || error?.response?.data?.error || error?.message;
	return (
		["ERR_SESSION_EXPIRED", "jwt expired", "jwt malformed"].includes(errorMsg) ||
		String(errorMsg || "").startsWith("Invalid token.")
	);
};

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});

	/*
	 * Os interceptors sao registrados uma vez e removidos na saida.
	 *
	 * Antes ficavam soltos no corpo do hook: cada render empilhava um par
	 * novo no cliente axios, que e global. Numa sessao aberta o dia inteiro
	 * a lista crescia sem limite, e toda requisicao passava por todos eles
	 * — inclusive por copias antigas, presas a estados ja descartados.
	 */
	useEffect(() => {
		const idDaRequisicao = api.interceptors.request.use(
			config => {
				const token = localStorage.getItem("token");
				if (token) {
					config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
					setIsAuth(true);
				}
				return config;
			},
			error => Promise.reject(error)
		);

		const idDaResposta = api.interceptors.response.use(
			response => response,
			async error => {
				const originalRequest = error.config;

				/*
				 * Sessao encerrada por inatividade pelo SERVIDOR.
				 *
				 * Vem com motivo proprio para nao ser confundida com token vencido:
				 * tentar renovar aqui seria inutil, porque a renovacao tambem e
				 * recusada. Melhor limpar e explicar do que deixar a tela dando erro
				 * sem dizer por que.
				 */
				const motivoDoErro =
					error?.response?.data?.error || error?.response?.data?.message;

				if (motivoDoErro === "ERR_SESSION_IDLE") {
					localStorage.removeItem("token");
					api.defaults.headers.Authorization = undefined;
					setIsAuth(false);
					toast.info("Sua sessao foi encerrada por inatividade.", { autoClose: 8000 });
					return Promise.reject(error);
				}
				if (error?.response?.status === 403 && isTokenError(error) && !originalRequest._retry) {
					originalRequest._retry = true;

					try {
						const { data } = await api.post("/auth/refresh_token");
						if (data) {
							localStorage.setItem("token", JSON.stringify(data.token));
							api.defaults.headers.Authorization = `Bearer ${data.token}`;
						}
						return api(originalRequest);
					} catch (refreshError) {
						localStorage.removeItem("token");
						api.defaults.headers.Authorization = undefined;
						setIsAuth(false);
						return Promise.reject(refreshError);
					}
				}
				if (error?.response?.status === 401) {
					localStorage.removeItem("token");
					api.defaults.headers.Authorization = undefined;
					setIsAuth(false);
				}
				return Promise.reject(error);
			}
		);

		return () => {
			api.interceptors.request.eject(idDaRequisicao);
			api.interceptors.response.eject(idDaResposta);
		};
	}, []);

	useEffect(() => {
		const token = localStorage.getItem("token");
		(async () => {
			if (token) {
				try {
					api.defaults.headers.Authorization = `Bearer ${JSON.parse(token)}`;
					const { data } = await api.post("/auth/refresh_token");
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
					localStorage.setItem("token", JSON.stringify(data.token));
					setIsAuth(true);
					setUser(data.user);
					if (data.user?.mustChangePassword && history.location.pathname !== "/login") {
						history.push("/login");
					}
				} catch (err) {
					localStorage.removeItem("token");
					api.defaults.headers.Authorization = undefined;
					setIsAuth(false);
				}
			}
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === user.id) {
				setUser(data.user);
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user]);

	/*
	 * Conta dedicada ao painel de grupos nao entra no sistema de atendimento.
	 *
	 * O painel roda em outra porta e autentica na mesma API, entao o login em
	 * si tem de funcionar — o que nao pode e essa conta abrir o sistema de
	 * atendimento. A recusa acontece aqui, antes de guardar o token: deixar
	 * entrar e depois esconder as telas dava tela branca e, pior, a pessoa
	 * seguia conseguindo chamar a API pela URL.
	 */
	const somenteDoPainel = usuario => {
		const permissoes = usuario?.permissions || {};
		if (usuario?.profile === "admin") return false;
		if (permissoes["panel.groups"] !== true) return false;

		return !Object.entries(permissoes).some(
			([chave, valor]) => chave !== "panel.groups" && valor === true
		);
	};

	const handleLogin = async userData => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData);

			if (somenteDoPainel(data.user)) {
				const painel = getPanelUrl();
				toast.error(
					"Esta conta nao tem permissao para o sistema de atendimento. " +
						`O acesso dela e o painel de grupos${painel ? `: ${painel}` : "."}`,
					{ autoClose: 12000 }
				);
				setLoading(false);
				// Sem lancar erro: o catch abaixo mostraria uma segunda mensagem,
				// tecnica, em cima da explicacao que a pessoa precisa ler.
				return null;
			}

			localStorage.setItem("token", JSON.stringify(data.token));
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsAuth(true);
			history.push(data.user?.mustChangePassword ? "/login" : getDefaultRoute(data.user));
			setLoading(false);
			return data.user;
		} catch (err) {
			toastError(err);
			setLoading(false);
			throw err;
		}
	};

	const handleChangePassword = async passwordData => {
		setLoading(true);

		try {
			const { data } = await api.post("/users/change-password", passwordData);
			setUser(data.user);
			history.push("/tickets");
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
			throw err;
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			await api.delete("/auth/logout");
			setIsAuth(false);
			setUser({});
			localStorage.removeItem("token");
			// Sair esquece a tela: quem entra depois comeca do proprio inicio.
			try { sessionStorage.removeItem("rsdesk:ultima-tela"); } catch (err) { /* modo restrito */ }
			api.defaults.headers.Authorization = undefined;
			setLoading(false);
			history.push("/login");
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	return { isAuth, user, loading, handleLogin, handleLogout, handleChangePassword };
};

export default useAuth;
